import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, Grid, TextField } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import SearchService from 'services/api/SearchService';
import useRequest from 'hooks/useRequest';
import { ConfigContext } from 'components/ConfigGuard';
import { FormProps, getRequiredValidationRule, getValidationErrorProps } from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { SearchResponse, SearchResult } from 'store/types/Search';

interface CompanyLookupFormItemProps extends FormProps {
  fieldName: string;
  filter?: string;
}

interface CompanyOption {
  score: number;
  internalId: string;
  customerId?: string;
  companyName?: string;
  isPerson?: boolean;
}

type QueryMode = 'autocomplete' | 'exact';

const MIN_CHARACTERS = 3;
const DEFAULT_PRE_FILTER_QUERY = 'customer_isPerson:false';

export function mapMemberCompanyResult(result: SearchResult): CompanyOption {
  const model: CompanyOption = {
    internalId: result.internalId,
    score: result.score,
  };

  for (const field of result.fields) {
    switch (field.key) {
      case 'customer_id':
        model.customerId = field.value;
        break;

      case 'companyName':
        model.companyName = field.value;
        break;

      case 'isPerson':
      case 'customer_isPerson':
        model.isPerson = field.value === 'true';
        break;
    }
  }

  return model;
}

export function mapMemberCompanyResults(response?: SearchResponse): CompanyOption[] {
  return response?.results?.map(mapMemberCompanyResult) ?? [];
}

const escapeLuceneTerm = (value: string) => value.replace(/([+\-!(){}[\]^"~*?:\\/]|&&|\|\|)/g, '\\$1');

const escapeLucenePhrase = (value: string) => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const buildBaseQueryParts = (preFilterQuery?: string): string[] => {
  const safePreFilter = preFilterQuery?.trim() ? `(${preFilterQuery.trim()})` : undefined;

  return [DEFAULT_PRE_FILTER_QUERY, safePreFilter].filter(Boolean) as string[];
};

const buildAutocompleteCompanyQuery = (value: string, preFilterQuery?: string): string => {
  const terms = value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((term, index, arr) => {
      const escaped = escapeLuceneTerm(term);
      const isLast = index === arr.length - 1;

      return `companyName:${isLast ? `${escaped}*` : escaped}`;
    });

  return [...buildBaseQueryParts(preFilterQuery), ...terms].join(' AND ');
};

const buildExactCompanyQuery = (value: string, preFilterQuery?: string): string => {
  const escaped = escapeLucenePhrase(value.trim());

  return [...buildBaseQueryParts(preFilterQuery), `companyName:"${escaped}"`].join(' AND ');
};

const CompanyLookupFormItem: React.FunctionComponent<CompanyLookupFormItemProps> = ({
  fieldName,
  required,
  disabled,
  filter,
}) => {
  const { control } = useFormContext();
  const { search } = useContext(ConfigContext);
  const fieldValue = useWatch({ control, name: fieldName });
  const [inputValue, setInputValue] = useState(fieldValue ?? '');
  const [queryMode, setQueryMode] = useState<QueryMode>(fieldValue ? 'exact' : 'autocomplete');

  useEffect(() => {
    setInputValue(fieldValue ?? '');
    setQueryMode(fieldValue ? 'exact' : 'autocomplete');
  }, [fieldValue]);

  const query = useMemo(() => {
    const trimmedValue = inputValue?.trim();

    if (!trimmedValue || trimmedValue.length < MIN_CHARACTERS) {
      return undefined;
    }

    return queryMode === 'exact'
      ? buildExactCompanyQuery(trimmedValue, filter)
      : buildAutocompleteCompanyQuery(trimmedValue, filter);
  }, [inputValue, queryMode, filter]);

  const companySearchRequest = useMemo(
    () =>
      search?.companySearchConfigurationId && query
        ? () => SearchService.search(search.companySearchConfigurationId, query)
        : undefined,
    [query, search?.companySearchConfigurationId]
  );

  const { data, loading } = useRequest<SearchResponse>(companySearchRequest);
  const options = useMemo(() => mapMemberCompanyResults(data), [data]);

  return (
    <Grid {...defaultGridContainerProps}>
      <Grid {...defaultGridItemProps}>
        <Controller
          name={fieldName}
          control={control}
          rules={{
            required: getRequiredValidationRule('your answer', true, required),
          }}
          render={({ field, fieldState: { error } }) => {
            const selectedOption = options.find((option) => option.companyName === field.value) ?? null;

            return (
              <Autocomplete<CompanyOption, false, false, false>
                disabled={disabled}
                loading={loading}
                options={options}
                value={selectedOption}
                inputValue={inputValue}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'input') {
                    setQueryMode('autocomplete');
                    setInputValue(newInputValue);
                  }

                  if (reason === 'clear') {
                    setQueryMode('autocomplete');
                    setInputValue('');
                    field.onChange('');
                  }
                }}
                onChange={(_, newValue) => {
                  const companyName = newValue?.companyName ?? '';

                  setQueryMode(companyName ? 'exact' : 'autocomplete');
                  setInputValue(companyName);
                  field.onChange(companyName);
                }}
                isOptionEqualToValue={(option, value) => option.internalId === value.internalId}
                getOptionLabel={(option) => option.companyName ?? ''}
                filterOptions={(x) => x}
                noOptionsText={
                  inputValue?.trim().length < MIN_CHARACTERS
                    ? `Type at least ${MIN_CHARACTERS} characters`
                    : 'No companies found'
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.internalId}>
                    {option.companyName ?? ''}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required={required}
                    {...getValidationErrorProps(error)}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            );
          }}
        />
      </Grid>
    </Grid>
  );
};

export default CompanyLookupFormItem;
